import React, { useState, useEffect } from "react";
import { DatePicker, TimePicker } from "antd";
import moment from "moment";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import BusinessHeader from "../BusinessHeader";
import SidebarAdmin from "../SidebarAdmin";
import config from "../../config";
import { useForm } from "react-hook-form";

const AddAppointments = () => {
  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_id: "",
    firstName: "",
    lastName: "",
    appointmentFor: "",
    phone: "",
    email: "",
    doctorName: "",
    startDate: moment(),
    startTime: moment().add(1, "hours"),
  });

  const [doctors, setDoctors] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [digitCount, setDigitCount] = useState(0);
  const [requiredDigits, setRequiredDigits] = useState(10);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [phoneError, setPhoneError] = useState("");
  const [fieldsLocked, setFieldsLocked] = useState(true);
  const [userExists, setUserExists] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [hasPermission, setHasPermission] = useState(null); // null indicates loading

  // Fetch user permissions
  const fetchPermissions = async () => {
    try {
      const businessID = localStorage.getItem('BusinessID');
      const userID = localStorage.getItem('userUUID');

      if (!businessID || !userID) {
        console.error("Business ID or User ID not found in localStorage");
        setHasPermission(false);
        return;
      }

      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: businessID, user_id: userID }),
      });

      const data = await response.json();
      if (data.status === "success" && data.permissions) {
        setHasPermission(data.permissions.add_appointment === 1);
      } else {
        console.error("Failed to fetch permissions:", data.message);
        setHasPermission(false);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setHasPermission(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const businessID = localStorage.getItem("BusinessID");
      if (!businessID) {
        console.error("Business ID not found in localStorage");
        alert("Business ID not found. Please log in again.");
        return;
      }

      const response = await fetch(
        `${config.API_URL}get_business_doctors.php?business_id=${businessID}`
      );
      const data = await response.json();

      if (data.status === "success") {
        const formattedDoctors = data.doctors.map((doc) => ({
          value: doc.doctor_id,
          label: doc.full_doctor_name,
        }));
        setDoctors(formattedDoctors);
      } else {
        console.error("Failed to fetch doctors:", data.message);
        alert("Failed to fetch doctors: " + data.message);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
      alert("An error occurred while fetching doctors.");
    } finally {
      setLoadingDoctors(false);
    }
  };

  const fetchUserDetails = async (phoneNumber) => {
    try {
      const phoneForApi = phoneNumber.startsWith("+") ? phoneNumber.substring(1) : phoneNumber;

      const response = await fetch(`${config.API_URL}get_patient_profile.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneForApi }),
      });

      const data = await response.json();
      console.log("User details response:", data);

      if (data.user_exists) {
        setFormData({
          ...formData,
          user_id: data.user_id,
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email || "",
          phone: data.phone || phoneNumber,
        });
        reset({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || "",
          phone: data.phone || phoneNumber,
        });
        setUserExists(true);
        setFieldsLocked(false);
      } else {
        setFormData({
          ...formData,
          user_id: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: phoneNumber,
        });
        reset({
          firstName: "",
          lastName: "",
          email: "",
          phone: phoneNumber,
        });
        setUserExists(false);
        setFieldsLocked(false);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      alert("An error occurred while fetching user details.");
    }
  };

  const validatePhoneNumber = (phoneNumber, countryCode) => {
    const phoneLength = phoneNumber.length;
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
        return phoneLength === 10;
      case "ae":
        return phoneLength === 9;
      case "ng":
        return phoneLength === 10;
      case "lk":
        return phoneLength === 9;
      case "bd":
        return phoneLength === 10;
      default:
        return false;
    }
  };

  const getRequiredDigits = (countryCode) => {
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
      case "bd":
        return 10;
      case "ae":
      case "lk":
        return 9;
      case "ng":
        return 11;
      default:
        return 10;
    }
  };

  const handlePhoneNumberChange = (value, country) => {
    setPhoneNumber(value);
    const digits = value.replace(country.dialCode, "");
    setDigitCount(digits.length);
    const digitsRequired = getRequiredDigits(country.countryCode);
    setRequiredDigits(digitsRequired);

    const formattedPhone = "+" + value.replace(/\D/g, "");

    const isValid = validatePhoneNumber(value.replace(country.dialCode, ""), country.countryCode);
    if (!isValid) {
      setPhoneError(`Invalid phone number length for ${country.name}`);
      setIsPhoneValid(false);
      setFieldsLocked(true);
      console.log("Phone validation failed:", phoneError);
    } else {
      setPhoneError("");
      setIsPhoneValid(true);
      setFieldsLocked(false);
      console.log("Phone validated successfully:", formattedPhone);
    }

    setFormData((prevState) => ({
      ...prevState,
      phone: formattedPhone,
    }));

    if (isValid) {
      fetchUserDetails(formattedPhone);
    }
  };

  const handleDateChange = (date) => {
    if (date && date.isValid()) {
      setFormData({ ...formData, startDate: date });
      setValue("startDate", date.format("YYYY-MM-DD"));
    }
  };

  const handleTimeChange = (time) => {
    if (time && time.isValid()) {
      setFormData({ ...formData, startTime: time });
      setValue("startTime", time.format("HH:mm"));
    }
  };

  const handleFormSubmit = async () => {
    const businessID = localStorage.getItem("BusinessID");
    if (!businessID) {
      alert("Business ID not found. Please log in again.");
      return;
    }

    if (!formData.doctorName || !formData.startDate || !formData.startTime) {
      alert("Doctor, date, and time are required fields.");
      return;
    }

    if (!userExists && (!formData.firstName || !formData.lastName || !formData.phone)) {
      alert("First name, last name, and phone number are required for new users.");
      return;
    }

    const appointmentData = {
      business_id: businessID,
      doctor_id: formData.doctorName.value,
      user_id: formData.user_id || "",
      first_name: formData.firstName,
      last_name: formData.lastName,
      appointment_for: formData.appointmentFor || "",
      email: formData.email || "",
      phone: formData.phone,
      date: formData.startDate.format("YYYY-MM-DD"),
      time: formData.startTime.format("HH:mm"),
    };

    console.log("Submitting appointment data:", appointmentData);

    try {
      const response = await fetch(`${config.API_URL}book_appointment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(appointmentData),
      });

      const data = await response.json();
      console.log("Book appointment response:", data);

      if (data.status === "success") {
        alert("Appointment booked successfully!");
        resetForm();
        navigate("/appoinmentlistbusiness");
      } else {
        alert("Failed to book appointment: " + data.message);
        console.warn("Failed to book appointment:", data.message);
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      alert("An error occurred while booking the appointment.");
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      firstName: "",
      lastName: "",
      appointmentFor: "",
      phone: "",
      email: "",
      doctorName: "",
      startDate: moment(),
      startTime: moment().add(1, "hours"),
    });
    setPhoneNumber("");
    setPhoneError("");
    setIsPhoneValid(false);
    setFieldsLocked(true);
    setUserExists(false);
    reset({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      appointmentFor: "",
    });
  };

  useEffect(() => {
    fetchPermissions();
    fetchDoctors();
  }, []);

  // Render loading state while checking permissions
  if (hasPermission === null) {
    return (
      <div>
        <BusinessHeader />
        <SidebarAdmin />
        <div className="page-wrapper">
          <div className="content">
            <p>Loading permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render access denied message if user lacks permission
  if (!hasPermission) {
    return (
      <div>
        <BusinessHeader />
        <SidebarAdmin />
        <div className="page-wrapper">
          <div className="content">
            <div className="alert alert-danger">
              You do not have permission to add appointments.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render the form if user has permission
  return (
    <div>
      <BusinessHeader />
      <SidebarAdmin
        id="menu-item4"
        id1="menu-items4"
        activeClassName="add-appoinment"
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Appointment</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Add Appointment</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-heading">
                          <h4>Patient Details</h4>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-group local-forms">
                          <label>
                            Phone Number <span className="login-danger">*</span>
                          </label>
                          <PhoneInput
                            onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                            country={"in"}
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            className="form-control phoneinput"
                            placeholder="Enter phone number"
                            countryCodeEditable={false}
                            inputStyle={{ width: "100%", height: "45px" }}
                            masks={{
                              in: "..........",
                              gb: "..........",
                              us: "..........",
                              ae: ".........",
                              ng: "..........",
                              lk: ".........",
                              bd: "..........",
                            }}
                          />
                          <div className="mt-2 d-flex justify-content-between">
                            {phoneError && <div className="text-danger">{phoneError}</div>}
                            <div></div>
                            {digitCount}/{requiredDigits}
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>
                            First Name <span className="login-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            {...register("firstName", { required: "First name is required" })}
                            value={formData.firstName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                firstName: e.target.value
                                  .split(/(\s+|\()/g)
                                  .map((part, index, arr) =>
                                    part.length > 0
                                      ? part === "(" && index < arr.length - 1
                                        ? "("
                                        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                                      : part
                                  )
                                  .join(""),
                              })
                            }
                            onKeyPress={(e) => {
                              if (!/[A-Za-z]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={fieldsLocked || userExists}
                          />
                          {errors.firstName && (
                            <p className="text-danger">{errors.firstName.message}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-6 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>
                            Last Name <span className="login-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            {...register("lastName", { required: "Last name is required" })}
                            value={formData.lastName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                lastName: e.target.value
                                  .split(/(\s+|\()/g)
                                  .map((part, index, arr) =>
                                    part.length > 0
                                      ? part === "(" && index < arr.length - 1
                                        ? "("
                                        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                                      : part
                                  )
                                  .join(""),
                              })
                            }
                            onKeyPress={(e) => {
                              if (!/[A-Za-z]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={fieldsLocked || userExists}
                          />
                          {errors.lastName && (
                            <p className="text-danger">{errors.lastName.message}</p>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-12 col-xl-12">
                        <div className="form-group local-forms">
                          <label>Email</label>
                          <input
                            type="email"
                            className="form-control"
                            {...register("email")}
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            disabled={fieldsLocked || userExists}
                          />
                        </div>
                      </div>
                      <div className="col-6 col-md-6 col-xl-6">
                        <div className="form-group local-forms cal-icon">
                          <label>
                            Date of Appointment <span className="login-danger">*</span>
                          </label>
                          <DatePicker
                            className="form-control datetimepicker"
                            onChange={handleDateChange}
                            value={formData.startDate}
                            suffixIcon={null}
                            style={{ width: "100%" }}
                            disabled={fieldsLocked}
                            format="YYYY-MM-DD"
                          />
                        </div>
                      </div>
                      <div className="col-6 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>
                            Time <span className="login-danger">*</span>
                          </label>
                          <TimePicker
                            className="form-control"
                            onChange={handleTimeChange}
                            value={formData.startTime}
                            format="HH:mm"
                            style={{ width: "100%" }}
                            disabled={fieldsLocked}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>
                            Consulting Doctor <span className="login-danger">*</span>
                          </label>
                          <Select
                            value={formData.doctorName}
                            onChange={(selected) =>
                              setFormData({ ...formData, doctorName: selected })
                            }
                            options={doctors}
                            menuPortalTarget={document.body}
                            isLoading={loadingDoctors}
                            isDisabled={fieldsLocked}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderColor: state.isFocused ? "none" : "2px solid rgba(46, 55, 164, 0.1);",
                                boxShadow: state.isFocused ? "0 0 0 1px #2e37a4" : "none",
                                "&:hover": {
                                  borderColor: state.isFocused ? "none" : "2px solid rgba(46, 55, 164, 0.1)",
                                },
                                borderRadius: "10px",
                                fontSize: "14px",
                                minHeight: "45px",
                              }),
                              dropdownIndicator: (base, state) => ({
                                ...base,
                                transform: state.selectProps.menuIsOpen ? "rotate(-180deg)" : "rotate(0)",
                                transition: "250ms",
                                width: "35px",
                                height: "35px",
                              }),
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-xl-6">
                        <div className="form-group local-forms">
                          <label>Appointment For (optional)</label>
                          <input
                            type="text"
                            className="form-control"
                            {...register("appointmentFor")}
                            value={formData.appointmentFor}
                            onChange={(e) =>
                              setFormData({ ...formData, appointmentFor: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className=" CHIcol-12">
                        <div className="doctor-submit text-end">
                          <button
                            type="submit"
                            className="btn btn-primary submit-form me-2"
                            disabled={!isPhoneValid}
                          >
                            Submit
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary cancel-form"
                            onClick={resetForm}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAppointments;